/**
 * ehr#233 - the Demographics "Appointment Reminders" tab (Chase's
 * APPOINTMENT_REMINDERS_MODAL_10-04 V2). Pure helpers, so the drawer stays thin.
 *
 * Stored in the demographics blob under section `reminders`, as strings like every
 * other demographics field: `voiceAi` / `sms` / `email` = 'true' | 'false' | '' and
 * `<channel>At` = the ISO time that answer was recorded. Consent rule (reminders
 * "decision 17", ruled by Jay 2026-10-05): a blank answer means okay for appointment
 * reminders, and 'false' is the client saying no. The API copies the answers into the
 * contacts okCall / okText / okEmail columns on save.
 *
 * Chase's 10-05 reopen (Jay, 10-09: decision 17 stands): a channel nobody has asked about
 * shows a neutral "Not asked" switch, not a Yes nobody set; and Contact Info's "OK to text"
 * on the mobile phone (`contact.mobileOkText`) is the same answer as Text message.
 */

export type ReminderChannel = 'voiceAi' | 'sms' | 'email';

export const REMINDER_CHANNELS: {
  key: ReminderChannel;
  name: string;
  desc: string;
}[] = [
  {
    key: 'voiceAi',
    name: 'Voice AI call',
    desc: 'An automated phone call reads the appointment details and can take a confirmation or reschedule request.',
  },
  {
    key: 'sms',
    name: 'Text message',
    desc: 'A short SMS with the date, time and location. Standard message rates may apply.',
  },
  {
    key: 'email',
    name: 'Email',
    desc: 'An email with the appointment details and a calendar attachment.',
  },
];

export type ChannelState = { okay: boolean; recorded: boolean; at: string };

/** One channel's state from the section's stored strings. */
export function channelState(
  sec: Record<string, string> | undefined,
  ch: ReminderChannel,
): ChannelState {
  const v = sec?.[ch] ?? '';
  return {
    okay: v !== 'false',
    recorded: v === 'true' || v === 'false',
    at: sec?.[`${ch}At`] ?? '',
  };
}

/** What the switch shows: the recorded answer, or Not asked. */
export type ChannelView = 'yes' | 'no' | 'unasked';
export function channelView(s: ChannelState): ChannelView {
  if (!s.recorded) return 'unasked';
  return s.okay ? 'yes' : 'no';
}
export const VIEW_TEXT: Record<ChannelView, string> = {
  yes: 'Yes',
  no: 'No',
  unasked: 'Not asked',
};

/** A click on the switch: Not asked or No records Yes; Yes records No. */
export function nextAnswer(view: ChannelView): boolean {
  return view !== 'yes';
}

/** Contact Info's "OK to text" (on the mobile phone): the Text message answer. */
export const OK_TEXT_KEY = 'mobileOkText';

/**
 * ehr#233 (Jay 2026-10-09): Contact Info's mobile "OK to call" / "OK to text" are the dated
 * Voice AI call / Text message answers (the ones synced with the Opt-Out Center's care switches).
 */
export const CONTACT_CHANNEL_KEYS: Record<string, ReminderChannel> = {
  mobileOkCall: 'voiceAi',
  [OK_TEXT_KEY]: 'sms',
};

/** The two field writes for flipping a channel: the answer and when it was recorded. */
export function channelAnswer(
  ch: ReminderChannel,
  okay: boolean,
  nowIso: string,
): [string, string][] {
  return [
    [ch, okay ? 'true' : 'false'],
    [`${ch}At`, nowIso],
  ];
}

/** "Oct 5, 2026" from an ISO time; '' when missing or invalid. */
export function fmtRecorded(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The line under a channel saying what is true and since when. */
export function stampLine(s: ChannelState): string {
  const when = fmtRecorded(s.at);
  if (!s.recorded) return 'Not asked yet. Reminders go out unless the client says no.';
  return s.okay
    ? `Okay to contact${when ? ` since ${when}` : ''}`
    : `Not okay to contact${when ? `, recorded ${when}` : ''}`;
}

/** Where a channel's reminder would go, from the Contact Info section. */
export function destinationLine(
  ch: ReminderChannel,
  contact: Record<string, string> | undefined,
): { text: string; missing: boolean } {
  const phone = (contact?.mobilePhone ?? '').trim();
  const email = (contact?.email ?? '').trim();
  if (ch === 'email')
    return email
      ? { text: `Sends to ${email}`, missing: false }
      : { text: 'No email on file', missing: true };
  if (!phone) return { text: 'No mobile phone on file', missing: true };
  return { text: `${ch === 'sms' ? 'Texts' : 'Calls'} ${phone}`, missing: false };
}
